/**
 * Season 1 — quanto do mundo já está escrito, coleção por coleção.
 *
 * <p>A pergunta que a tela responde é de entrega, e não de lore: <i>dá para abrir a
 * Season 1?</i> Por isso ela mora em Gestão de Projeto, e não em Criação do Mundo.
 *
 * <p><b>O selo do topo é a tela.</b> As dezessete barras respondem "o que falta em cada
 * coleção", e essa é a segunda pergunta; a primeira é "quanto falta, no total". Ela
 * não se responde somando dezessete linhas com o olho, e por isso ganhou o maior
 * elemento da página.
 *
 * <p>A meta é um número <b>por região</b>, igual para todas, e a tela mostra esse
 * número vezes as três da Season (decisão do Theo). A multiplicação aparece escrita ao
 * lado de cada barra — sem isso, "45" pareceria um total combinado à mão, e ninguém
 * saberia que mexer nele mexe em 15.
 *
 * <p>Meta zero não vira uma barra cheia nem uma divisão por zero: vira "meta a definir".
 * As dezessete nascem assim, e a tela precisa dizer isso em vez de fingir 0/0 = 100%.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { IconeDeRegiao } from './mundo/IconeDeRegiao';
import * as mundoApi from './mundo/mundoApi';
import { progressoGeral } from './mundo/progressoGeral';
import type { LinhaDeProgresso, Progresso as PainelDeProgresso } from './mundo/tipos';
import { IconeDoMenu } from '../shell/IconeDoMenu';
import { TituloDaPagina } from '../shell/TituloDaPagina';
import './progresso.css';

const CHAVE = ['mundo', 'progresso'];

/**
 * Os blocos do painel, na ordem em que se lê o mundo: o mapa, quem o habita, o que
 * vive nele, o que se carrega, o que se joga e o baralho.
 *
 * <p>A cor é do bloco e não da linha — é a mesma regra da barra lateral, e pela mesma
 * razão: dezessete cores viram mostruário, e o olho não agrupa nada. Com seis, a mancha
 * de cor já separa "mundo" de "itens" antes de qualquer palavra ser lida.
 *
 * <p>O ícone, ao contrário, é de cada linha. Ele responde "o que é isto", que é uma
 * pergunta por coleção — e é por isso que existem uma bigorna, uma picareta, um saco e
 * um baú, em vez de oito frascos de poção iguais nas oito barras de item.
 */
const BLOCOS: { titulo: string; cor: string; linhas: { colecao: string; icone: string }[] }[] = [
  {
    titulo: 'O mapa',
    cor: '#C9A25A',
    linhas: [
      { colecao: 'areas', icone: 'ilha' },
      { colecao: 'areas_completas', icone: 'hexagono-feito' },
    ],
  },
  // Povos e Vida eram dois blocos de duas linhas, e a divisão entre eles era teórica:
  // raça, facção, NPC e criatura respondem todos "quem habita a ilha". Juntos viram um
  // bloco de quatro, que é o tamanho em que um bloco começa a valer como bloco.
  {
    titulo: 'Povoamento',
    cor: '#7EA65B',
    linhas: [
      { colecao: 'racas', icone: 'elfo' },
      { colecao: 'faccoes', icone: 'estandarte' },
      { colecao: 'npcs', icone: 'personagem' },
      { colecao: 'criaturas', icone: 'felino' },
    ],
  },
  {
    titulo: 'Itens',
    cor: '#5B8FB9',
    linhas: [
      { colecao: 'itens:equipamento', icone: 'espada' },
      { colecao: 'itens:ferramenta', icone: 'picareta' },
      { colecao: 'itens:coletavel', icone: 'losango' },
      { colecao: 'itens:craft', icone: 'bigorna' },
      { colecao: 'itens:consumivel', icone: 'pocao' },
      { colecao: 'itens:dropavel', icone: 'saco' },
      { colecao: 'itens:tesouro', icone: 'bau' },
      { colecao: 'itens:quest', icone: 'chave' },
    ],
  },
  {
    titulo: 'Narrativa',
    cor: '#C0703C',
    linhas: [
      { colecao: 'quests', icone: 'rosacea' },
      { colecao: 'eventos', icone: 'losango-duplo' },
    ],
  },
  { titulo: 'Álbum de Cartas', cor: '#A87BD0', linhas: [{ colecao: 'cartas', icone: 'carta' }] },
];

export function Progresso() {
  const fila = useQueryClient();
  const [editando, setEditando] = useState(false);

  const { data: painel, isLoading, error } = useQuery<PainelDeProgresso>({
    queryKey: CHAVE,
    queryFn: mundoApi.carregarProgresso,
  });

  // O PATCH devolve o painel inteiro recalculado, então não há por que reconsultar:
  // a resposta já é o novo estado, e invalidar renderizaria um piscar de dados velhos.
  const salvar = useMutation({
    mutationFn: ({ colecao, porRegiao }: { colecao: string; porRegiao: number }) =>
      mundoApi.salvarMeta(colecao, porRegiao),
    onSuccess: (novo) => fila.setQueryData(CHAVE, novo),
  });

  if (isLoading) return <div className="pr-nada">Contando o mundo…</div>;
  if (error || !painel) return <div className="pr-nada">Não deu para carregar o progresso.</div>;

  const regioes = painel.regioes;
  // Quantas a Season tem, e não quantas já foram criadas: é este o número que
  // multiplica a meta, e mostrar o outro seria dois números que não fecham.
  const daSeason = painel.regioesDaSeason;
  const faltamCriar = daSeason - regioes.length;

  const porColecao = new Map(painel.linhas.map((l) => [l.colecao, l]));

  // A conta mora em `progressoGeral`, e não aqui: é a mesma que a barra lateral usa.
  // Duas cópias da régua foi o que fez o menu dizer 16/49 contra 16/21 da página.
  const geral = progressoGeral(painel.linhas);
  const faltamMetas = painel.linhas.filter((l) => l.meta <= 0).length;

  return (
    <div>
      <TituloDaPagina
        acoes={
          <button
            className={`pr-editar${editando ? ' on' : ''}`}
            onClick={() => setEditando((antes) => !antes)}
          >
            {editando ? 'Concluir' : 'Editar metas'}
          </button>
        }
      >
        A régua são as {daSeason} primeiras regiões do mapa: a meta de cada barra é o número
        por região multiplicado por elas. Conta toda ficha cadastrada, esteja ela ligada a
        uma área ou não — as duas barras de área são a exceção, porque toda área tem região.
      </TituloDaPagina>

      {/* ------------------------------------------------------- o selo */}

      <section className={`pr-selo${geral && geral.pct >= 100 ? ' pronta' : ''}`}>
        <div className="pr-selo-cima">
          <div>
            <div className="pr-selo-rot">Progresso da Ilha</div>
            <div className="pr-selo-sub">
              {geral
                ? `${geral.feito} de ${geral.meta} fichas combinadas`
                : 'Nenhuma meta combinada ainda — o painel não tem régua.'}
            </div>
          </div>
          <div className="pr-selo-pct">
            {geral == null ? '—' : <>{geral.pct}<span>%</span></>}
          </div>
        </div>

        <div className="pr-selo-trilho">
          <i style={{ width: `${geral?.pct ?? 0}%` }} />
        </div>

        <div className="pr-selo-baixo">
          {/* As regiões da Season vêm nomeadas de propósito: "3 regiões" é um número
              que ninguém consegue conferir, e o nome é. */}
          <div className="pr-season">
            {regioes.map((regiao) => (
              <span
                key={regiao.id}
                className="pr-regiao"
                style={{ '--c': regiao.cor ?? 'var(--gold)' } as React.CSSProperties}
              >
                <IconeDeRegiao icone={regiao.icone} cor={regiao.cor} tamanho={15} />
                {regiao.nome}
              </span>
            ))}

            {/* Slot da Season ainda vazio é dívida, e precisa aparecer: sem isto o
                painel mostraria três chips faltando um e ninguém repararia. */}
            {faltamCriar > 0 && (
              <span className="pr-season-vazia">
                {faltamCriar === daSeason
                  ? 'nenhuma região criada ainda'
                  : `${faltamCriar} ${faltamCriar === 1 ? 'região' : 'regiões'} por criar`}
              </span>
            )}
          </div>

          {faltamMetas > 0 && (
            <span className="pr-selo-aviso">
              {faltamMetas} {faltamMetas === 1 ? 'barra sem meta' : 'barras sem meta'} — fora
              da conta
            </span>
          )}
        </div>
      </section>

      {/* ------------------------------------------------------ os blocos */}

      {BLOCOS.map((bloco) => (
        <section
          key={bloco.titulo}
          className="pr-bloco"
          style={{ '--c': bloco.cor } as React.CSSProperties}
        >
          <h2 className="pr-bloco-tit">{bloco.titulo}</h2>

          <div className="pr-barras">
            {bloco.linhas.map(({ colecao, icone }) => {
              const linha = porColecao.get(colecao);
              if (!linha) return null;
              return (
                <Barra
                  key={colecao}
                  linha={linha}
                  icone={icone}
                  regioes={daSeason}
                  editando={editando}
                  salvar={(porRegiao) => salvar.mutate({ colecao, porRegiao })}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function Barra({
  linha,
  icone,
  regioes,
  editando,
  salvar,
}: {
  linha: LinhaDeProgresso;
  icone: string;
  regioes: number;
  editando: boolean;
  salvar(porRegiao: number): void;
}) {
  const semMeta = linha.meta <= 0;
  const fracao = semMeta ? 0 : Math.min(1, linha.feito / linha.meta);
  const cumprida = !semMeta && linha.feito >= linha.meta;
  const pct = semMeta ? null : Math.round(fracao * 100);

  return (
    <div className={`pr-linha${cumprida ? ' cheia' : ''}${semMeta ? ' sem-meta' : ''}`}>
      <span className="pr-ic" aria-hidden="true">
        <IconeDoMenu icone={icone} tamanho={17} />
      </span>

      <div className="pr-rotulo">{linha.rotulo}</div>

      <div className="pr-numeros">
        <b>{linha.feito}</b>
        {semMeta ? (
          <span className="pr-adefinir">sem meta</span>
        ) : (
          <span>/ {linha.meta}</span>
        )}
      </div>

      {/* Havia aqui um "+N sem área", com o que existia e não contava. A barra passou
          a medir o que está cadastrado, então não sobra resto para avisar. */}

      <div className="pr-trilho">
        <i style={{ width: `${fracao * 100}%` }} />
      </div>

      {/* O selo de cumprida troca o percentual: 100% é a informação menos útil da
          linha quando ela já acabou — o que importa ali é que acabou. */}
      <div className="pr-pct">
        {cumprida ? <span className="pr-feito">✓</span> : pct == null ? '' : `${pct}%`}
      </div>

      <div className="pr-meta">
        {editando ? (
          <CampoDeMeta valorInicial={linha.porRegiao} salvar={salvar} />
        ) : (
          <span className="pr-conta">
            {linha.porRegiao} × {regioes}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * O número por região. Salva ao sair do campo ou no Enter, e não a cada tecla: digitar
 * "12" passa por "1", e um PATCH por tecla gravaria a meta errada no caminho.
 */
function CampoDeMeta({
  valorInicial,
  salvar,
}: {
  valorInicial: number;
  salvar(porRegiao: number): void;
}) {
  const [valor, setValor] = useState(String(valorInicial));

  function confirmar() {
    const numero = Number(valor);
    if (!Number.isInteger(numero) || numero < 0) {
      setValor(String(valorInicial));
      return;
    }
    if (numero !== valorInicial) salvar(numero);
  }

  return (
    <input
      className="pr-campo"
      type="number"
      min={0}
      value={valor}
      aria-label="Meta por região"
      onChange={(e) => setValor(e.target.value)}
      onBlur={confirmar}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setValor(String(valorInicial));
          e.currentTarget.blur();
        }
      }}
    />
  );
}
